import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
  getNeuronDebugState,
  NEURON_DEBUG_EVENT_NAME,
} from "../utils/neuronDebugStore";

const truncateId = (value, head = 10, tail = 4) => {
  if (!value) return null;
  const str = String(value);
  if (str.length <= head + tail + 1) return str;
  return `${str.slice(0, head)}...${str.slice(-tail)}`;
};

const formatTime = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleTimeString("en-GB", {hour12: false});
  } catch {
    return iso;
  }
};

const SectionTitle = ({children, right}) => (
  <div className="mb-2 flex items-baseline justify-between gap-3 font-sans text-kicker font-semibold uppercase tracking-[0.14em] text-pencil">
    <span>{children}</span>
    {right && (
      <span className="truncate font-sans text-kicker font-normal normal-case tracking-normal text-ink-tertiary">
        {right}
      </span>
    )}
  </div>
);

const MetaLine = ({label, value, title}) => (
  <div className="grid grid-cols-[84px_minmax(0,1fr)] gap-2 font-sans text-meta leading-5">
    <span className="text-ink-tertiary">{label}</span>
    <span className="truncate font-medium text-ink" title={title || value || ""}>
      {value || <span className="text-ink-muted">-</span>}
    </span>
  </div>
);

const formatLatency = (value) =>
  typeof value === "number" ? `${Math.round(value)}ms` : null;

const formatScore = (value) => {
  if (typeof value !== "number") return null;
  const score = value <= 1 ? value * 100 : value;
  return `${Math.round(score)}%`;
};

const SummaryValue = ({label, value}) => (
  <div className="border-l-2 border-pencil/30 pl-2.5">
    <div className="font-sans text-kicker uppercase tracking-[0.09em] text-ink-tertiary">
      {label}
    </div>
    <div className="mt-0.5 truncate font-sans text-body font-semibold tabular-nums text-ink">
      {value || "-"}
    </div>
  </div>
);

const RecommendationPanel = ({snapshot}) => {
  if (!snapshot) {
    return (
      <p className="font-sans text-meta italic text-ink-tertiary">
        Awaiting /recommendations response.
      </p>
    );
  }

  const items = Array.isArray(snapshot.items) ? snapshot.items : [];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-2 grid grid-cols-3 gap-2">
        <SummaryValue label="Returned" value={snapshot.returnedCount} />
        <SummaryValue label="Latency" value={formatLatency(snapshot.processingTimeMs)} />
        <SummaryValue label="Request" value={truncateId(snapshot.requestId, 8, 4)} />
      </div>

      {items.length === 0 ? (
        <p className="font-sans text-meta italic text-ink-tertiary">
          Response captured, no displayable items.
        </p>
      ) : (
        <ol className="min-h-0 flex-1 space-y-1 overflow-y-auto pr-1">
          {items.map((item, index) => (
            <li
              key={`${item.id || item.title}-${index}`}
              className="rounded-sm border border-rule bg-paper px-3 py-2"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate font-serif text-body font-semibold leading-5 text-ink">
                    {index + 1}. {item.title}
                  </div>
                  <div className="mt-1 flex min-w-0 flex-wrap gap-x-3 gap-y-0.5 font-sans text-kicker text-ink-tertiary">
                    {item.category && (
                      <span className="truncate">{item.category}</span>
                    )}
                    {item.source && (
                      <span className="truncate">{item.source}</span>
                    )}
                    {item.id && (
                      <span className="truncate" title={item.id}>
                        id {truncateId(item.id, 7, 4)}
                      </span>
                    )}
                  </div>
                </div>
                {/* match = similarity before damping (what the cards show);
                    final = the score that actually ordered the list. */}
                <div className="flex shrink-0 flex-col items-end gap-0.5">
                  {formatScore(item.baseScore) && (
                    <span className="font-sans text-meta font-semibold tabular-nums text-pencil">
                      {formatScore(item.baseScore)}
                      <span className="ml-1 font-normal text-ink-muted">match</span>
                    </span>
                  )}
                  {formatScore(item.score) && (
                    <span className="font-sans text-kicker tabular-nums text-ink-muted">
                      {formatScore(item.score)} final
                    </span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

const BatchList = ({batches}) => {
  if (!batches || batches.length === 0) {
    return (
      <p className="font-sans text-meta italic text-ink-tertiary">
        No batches captured yet.
      </p>
    );
  }

  return (
    <ul className="space-y-1.5">
      {batches.map((entry, index) => (
        <li
          key={`${entry.timestamp}-${index}`}
          className="rounded-sm border border-rule bg-paper px-3 py-2 text-meta text-ink-secondary"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium text-ink">
              {entry.count} event{entry.count === 1 ? "" : "s"}
            </span>
            <span className="text-kicker text-ink-tertiary">
              {formatTime(entry.timestamp)}
            </span>
          </div>
          <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-kicker text-ink-tertiary">
            <span title={entry.sessionId || ""}>
              sid{" "}
              <span className="text-ink">
                {truncateId(entry.sessionId, 6, 4) || "-"}
              </span>
            </span>
            <span title={entry.requestId || ""}>
              rid{" "}
              <span className="text-ink">
                {truncateId(entry.requestId, 6, 4) || "-"}
              </span>
            </span>
          </div>
          {entry.events?.length > 0 && (
            <details className="mt-1">
              <summary className="cursor-pointer list-none text-kicker text-ink-tertiary hover:text-ink">
                show event rows
              </summary>
              <ul className="mt-1 max-h-24 space-y-0.5 overflow-y-auto border-l border-rule pl-2 text-kicker text-ink-secondary">
                {entry.events.map((ev, i) => (
                  <li key={i} className="truncate" title={ev.itemId || ""}>
                    <span className="text-ink">
                      {ev.source || "event"}
                    </span>{" "}
                    item{" "}
                    <span className="text-ink">
                      {truncateId(ev.itemId, 8, 4) || "-"}
                    </span>
                    {ev.eventType && (
                      <span className="text-ink-muted">
                        {" "}
                        event {truncateId(ev.eventType, 7, 4)}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </li>
      ))}
    </ul>
  );
};

const ViewInterceptorOverlay = React.forwardRef(
  ({firedCount, children, showDebug = true}, ref) => {
    const [debugState, setDebugState] = useState(() => getNeuronDebugState());

    useEffect(() => {
      if (typeof window === "undefined") {
        return undefined;
      }

      const handleDebugUpdate = (event) => {
        if (event?.detail) {
          setDebugState(event.detail);
          return;
        }
        setDebugState(getNeuronDebugState());
      };

      window.addEventListener(NEURON_DEBUG_EVENT_NAME, handleDebugUpdate);
      return () =>
        window.removeEventListener(
          NEURON_DEBUG_EVENT_NAME,
          handleDebugUpdate
        );
    }, []);

    const closeDebug = useCallback(() => {
      try {
        localStorage.setItem("neuron:viewInterceptorDebug", "false");
      } catch {
        // best effort
      }
      window.dispatchEvent(
        new CustomEvent("viewInterceptorToggle", {detail: {enabled: false}})
      );
    }, []);

    useEffect(() => {
      if (!showDebug) return undefined;
      const onKeyDown = (event) => {
        if (event.key === "Escape") closeDebug();
      };
      window.addEventListener("keydown", onKeyDown);
      return () => window.removeEventListener("keydown", onKeyDown);
    }, [showDebug, closeDebug]);

    const totalEvents = useMemo(
      () =>
        (debugState.batchRequests || []).reduce(
          (sum, batch) => sum + (batch.count || 0),
          0
        ),
      [debugState.batchRequests]
    );

    return (
      <div
        ref={ref}
        data-view-interceptor-debug={showDebug ? "true" : "false"}
        className="relative h-screen overflow-y-auto overflow-x-hidden"
      >
        {showDebug && (
          <div
            aria-hidden="true"
            className="pointer-events-none fixed left-1/2 top-[10%] z-40 flex h-[68vh] w-[92vw] max-w-6xl -translate-x-1/2 flex-col rounded-lg border-2 border-pencil bg-paper shadow-overlay sm:top-[12%] sm:h-[64vh]"
          >
            <button
              type="button"
              onClick={closeDebug}
              aria-hidden="false"
              aria-label="Close the view interceptor"
              title="Close (Esc)"
              className="pointer-events-auto absolute -right-3 -top-3 z-10 inline-flex size-8 items-center justify-center rounded-full border border-pencil bg-paper font-sans text-body font-semibold text-pencil shadow-raised transition-colors duration-150 ease-out hover:bg-pencil hover:text-pencil-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              &#215;
            </button>
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 pb-2 pt-3">
              <span className="text-meta font-semibold uppercase tracking-[0.18em] text-ink">
                View Interceptor
              </span>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-kicker uppercase tracking-wide text-ink-secondary">
                <span>
                  Fired{" "}
                  <span className="text-ink">{firedCount}</span>
                </span>
                <span>
                  Batches{" "}
                  <span className="text-ink">
                    {(debugState.batchRequests || []).length}
                  </span>
                </span>
                <span>
                  Events{" "}
                  <span className="text-ink">{totalEvents}</span>
                </span>
              </div>
            </div>

            <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 px-4 pb-4 md:grid-cols-[0.82fr_1.18fr]">
              <div className="pointer-events-auto flex min-h-0 flex-col gap-3 overflow-hidden rounded-md border border-rule bg-surface-1 p-3">
                <div>
                  <SectionTitle>Session</SectionTitle>
                  <div className="space-y-1">
                    <MetaLine
                      label="Session"
                      value={
                        truncateId(debugState.sessionId, 14, 6) || "Unavailable"
                      }
                      title={debugState.sessionId}
                    />
                    <MetaLine
                      label="Request"
                      value={truncateId(debugState.requestId, 14, 6)}
                      title={debugState.requestId}
                    />
                  </div>
                </div>

                <div className="flex min-h-0 flex-1 flex-col">
                  <SectionTitle
                    right={`last ${(debugState.batchRequests || []).length}`}
                  >
                    Event batches
                  </SectionTitle>
                  <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                    <BatchList batches={debugState.batchRequests} />
                  </div>
                </div>
              </div>

              <div className="pointer-events-auto flex min-h-0 flex-col rounded-md border border-rule bg-surface-1 p-3">
                <SectionTitle
                  right={
                    debugState.latestRecommendation?.updatedAt
                      ? formatTime(debugState.latestRecommendation.updatedAt)
                      : null
                  }
                >
                  Latest recommendations
                </SectionTitle>
                <RecommendationPanel snapshot={debugState.latestRecommendation} />
              </div>
            </div>
          </div>
        )}
        <div className="relative z-0 min-h-screen">{children}</div>
      </div>
    );
  }
);

ViewInterceptorOverlay.displayName = "ViewInterceptorOverlay";

export default ViewInterceptorOverlay;
